import React from "react";
import { useMemo } from "react";
import moment from "moment";
import { mapBodyWeights } from "../../../../views/athlete-portal/calendar/body-composition/body-composition.helper";
import {
  filterStatsbyDateRange,
  filterUnsetValues,
  mapUtcToDatePicker,
  sortStatsByDate,
} from "../../../../utils/daily-stats";

const tableTitle = {
  steps: "Steps",
  sleep: "Sleep",
  bodyWeight: "Bodyweight",
  waterIntake: "Water",
};

const useTrackGoals = ({
  goalType,
  startDate,
  units,
  goalTarget,
  dailyStats,
}) => {
  const currentDate = Number(moment().format("YYYYMMDD"));

  const weightUnit = units === "metric" ? "kg" : "lbs";

  const todayStats = useMemo(() => {
    return dailyStats.find(
      ({ utcDate }) =>
        Number(moment(currentDate, "YYYY-MM-DD").format("YYYYMMDD")) === utcDate
    );
  }, [dailyStats]);

  const filteredStats = useMemo(() => {
    // So to show the 30 days before the start of the goal and stuff
    const utcReference = Number(
      moment(startDate, "YYYYMMDD").subtract(30, "days").format("YYYYMMDD")
    );

    return dailyStats.filter((stat) => {
      const isInRange =
        stat.utcDate >= utcReference && stat.utcDate <= currentDate;

      return !!stat[goalType] && isInRange;
    });
  }, [dailyStats, startDate]);

  const streaks = useMemo(() => {
    // Sort dates in ascending order and parse the
    const sortedStats = filteredStats
      .filter((stat) => stat[goalType] >= Number(goalTarget))
      .map((stat) => moment(String(stat.utcDate), "YYYYMMDD"))
      .sort((a, b) => a.diff(b));

    // Initialize streak variables
    let currentStreak = 0;
    let bestStreak = 0;
    let tempStreak = 1;

    // Loop through sorted dates to calculate streaks
    for (let i = 1; i < sortedStats.length; i++) {
      const prevDate = sortedStats[i - 1];
      const currDate = sortedStats[i];

      // Check if current date is consecutive to previous
      if (currDate.diff(prevDate, "days") === 1) {
        tempStreak++;
      } else {
        // Reset tempStreak if the streak is broken
        tempStreak = 1;
      }

      // Update best streak
      bestStreak = Math.max(bestStreak, tempStreak);
    }

    // Calculate the current streak up to the provided currentDate
    const currDateMoment = moment(String(currentDate), "YYYYMMDD");
    for (let i = sortedStats.length - 1; i >= 0; i--) {
      const diff = currDateMoment.diff(sortedStats[i], "days");
      if (diff === currentStreak) {
        currentStreak++;
      } else {
        break;
      }
    }

    return { currentStreak, bestStreak };
  }, [filteredStats, currentDate]);

  const lastDaysAchieved = useMemo(() => {
    const utcReference = Number(
      moment(String(currentDate), "YYYYMMDD")
        .subtract(7, "days")
        .format("YYYYMMDD")
    );

    const daysAchieved = filteredStats.filter(
      (stat) =>
        stat.utcDate >= utcReference &&
        stat.utcDate <= currentDate &&
        stat[goalType] >= Number(goalTarget)
    );

    const numberOfDaysAchieved = daysAchieved.length;

    return numberOfDaysAchieved;
  }, [filteredStats]);

  const calendarDates = useMemo(() => {
    if (goalType !== "bodyWeight" && goalType !== "personalRecord") {
      const sortedStats = filteredStats
        .filter((stat) => stat[goalType] >= Number(goalTarget))
        .map((stat) => moment(String(stat.utcDate), "YYYYMMDD"))
        .sort((a, b) => a.diff(b));

      const markedDates = sortedStats.reduce((acc, utcDate) => {
        const formattedDate = moment(utcDate, "YYYYMMDD").format("YYYY-MM-DD");
        acc[formattedDate] = {
          marked: true,
          dotColor: "blue",
        };
        return acc;
      }, {});

      const markedDatesArray = Object.keys(markedDates).map(
        (date) => new Date(date)
      );

      const modifiers = { marked: markedDatesArray };
      const modifiersClassNames = { marked: "marked-date" };

      return { modifiers, modifiersClassNames };
    }

    return {};
  }, [filteredStats, currentDate]);

  const averageStat = useMemo(() => {
    const ascStats = filteredStats
      .slice()
      .sort((a, b) => a.utcDate - b.utcDate);

    let totalStat = 0;

    ascStats.forEach((stat) => {
      const { [goalType]: targetStat } = stat;

      totalStat += targetStat;
    });

    const avgStat =
      goalType === "waterIntake"
        ? Number((totalStat / (ascStats.length || 1)).toFixed(1))
        : Math.round(totalStat / (ascStats.length || 1));

    return avgStat;
  }, [filteredStats, goalType]);

  const chartData = useMemo(() => {
    if (goalType !== "bodyWeight") return filteredStats;
    else {
      const unitMultiplier = units === "metric" ? 1 : 2.205;

      const bodyweightData = filteredStats
        .map((data) => mapBodyWeights(data, unitMultiplier))
        .sort((a, b) => a.utcDate - b.utcDate);

      return bodyweightData;
    }
  }, [filteredStats, goalType]);

  const tableData = useMemo(() => {
    const columnsData = [
      {
        name: "Date",
        field: "date",
        columnClass: "date",
        itemClass: "date",
      },
      {
        name: tableTitle[goalType],
        field: goalType,
        columnClass: goalType,
        itemClass: goalType,
      },
    ];

    const dateTo = moment().format("MMM D, YYYY");
    const dateFrom = moment(startDate, "YYYYMMDD").format("MMM D, YYYY");

    const statsData = sortStatsByDate(
      filterUnsetValues(filteredStats, goalType)
    );

    const statsList = filterStatsbyDateRange({
      stats: statsData,
      dateTo,
      dateFrom,
    });

    const listData = mapUtcToDatePicker({
      stats: statsList,
      fieldToConvert: "utcDate",
      newField: "date",
    });

    return { columnsData, listData, dateTo, dateFrom };
  }, [filteredStats, goalType]);

  return {
    todayStats,
    filteredStats,
    streaks,
    lastDaysAchieved,
    weightUnit,
    calendarDates,
    averageStat,
    chartData,
    tableData,
  };
};

export default useTrackGoals;
